import type { ImportColumnDef } from './types';

/** Normalizes a header cell so trivial spelling/formatting differences don't block auto-mapping. */
export function normalizeHeader(h: unknown): string {
    return String(h ?? '')
        .replace(/^\uFEFF/, '')
        .replace(/['"]/g, '')
        .replace(/[:\-]+$/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

export type HeaderMatchConfidence = 'exact' | 'alias' | 'fuzzy' | 'none';

export interface HeaderMatchSuggestion {
    columnIndex?: number;
    confidence: HeaderMatchConfidence;
}

function fuzzyEquals(a: string, b: string): boolean {
    if (!a || !b) return false;
    const na = a.replace(/\s+/g, '');
    const nb = b.replace(/\s+/g, '');
    if (na === nb) return true;
    // Tolerate one column being a prefix/suffix of the other (e.g. "מזהה חיצוני" vs "מזהה חיצוני לקוח").
    if (na.length >= 3 && nb.length >= 3 && (na.startsWith(nb) || nb.startsWith(na))) return true;
    return false;
}

/**
 * Suggests, for every column definition, which raw header (by index) best matches it.
 * Never maps two columns to the same header index; the earlier column in `columns` wins
 * and the later one falls back to the next-best candidate so the caller can flag a conflict.
 */
export function suggestColumnMapping(
    columns: ImportColumnDef[],
    rawHeaders: unknown[],
): Record<string, HeaderMatchSuggestion> {
    const normalizedHeaders = rawHeaders.map((h) => normalizeHeader(h));
    const used = new Set<number>();
    const result: Record<string, HeaderMatchSuggestion> = {};

    const tryAssign = (key: string, index: number, confidence: HeaderMatchConfidence) => {
        if (used.has(index)) return false;
        result[key] = { columnIndex: index, confidence };
        used.add(index);
        return true;
    };

    // Pass 1: exact normalized match on the canonical label.
    columns.forEach((col) => {
        const idx = normalizedHeaders.findIndex((h) => h === normalizeHeader(col.label));
        if (idx >= 0) tryAssign(col.key, idx, 'exact');
    });

    // Pass 2: exact match on any declared alias.
    columns.forEach((col) => {
        if (result[col.key]) return;
        const aliases = (col.aliases ?? []).map(normalizeHeader);
        const idx = normalizedHeaders.findIndex((h) => aliases.includes(h));
        if (idx >= 0) tryAssign(col.key, idx, 'alias');
    });

    // Pass 3: loose/fuzzy match against label or aliases.
    columns.forEach((col) => {
        if (result[col.key]) return;
        const candidates = [col.label, ...(col.aliases ?? [])].map(normalizeHeader);
        const idx = normalizedHeaders.findIndex((h) => candidates.some((c) => fuzzyEquals(h, c)));
        if (idx >= 0) tryAssign(col.key, idx, 'fuzzy');
    });

    columns.forEach((col) => {
        if (!result[col.key]) result[col.key] = { confidence: 'none' };
    });

    return result;
}

/** Detects two destination fields pointed at the same source column - must never happen silently. */
export function findMappingConflicts(mapping: Record<string, number | undefined>): { columnIndex: number; keys: string[] }[] {
    const byIndex = new Map<number, string[]>();
    Object.entries(mapping).forEach(([key, idx]) => {
        if (idx == null) return;
        const list = byIndex.get(idx) ?? [];
        list.push(key);
        byIndex.set(idx, list);
    });
    return Array.from(byIndex.entries())
        .filter(([, keys]) => keys.length > 1)
        .map(([columnIndex, keys]) => ({ columnIndex, keys }));
}
